'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsScreen } from '@/app/hooks';
import { cn } from '@/app/utils';
import {
  HOME_HERO_BG_SRC_AGENT,
  HOME_HERO_BG_SRC_CREATE,
  HOME_HERO_POSTER_SRC_AGENT,
  HOME_HERO_POSTER_SRC_CREATE,
} from '../constants';
import { HomeGenerationForm } from './HomeGenerationForm';
import { HomeS1Roles } from './HomeS1Roles';

type HeroMode = 'create' | 'agent';

/** Home Hero：支持视频创作与 S1 角色体验两种模式。 */
export const HomeHero = () => {
  const t = useTranslations();
  const { isSmScreen } = useIsScreen();

  // 提示词框形态：create 显示生成表单；agent(Vidu S1) 显示角色卡片
  const [mode, setMode] = useState<HeroMode>('create');

  // 两种模式共用同一 grid，通过高度过渡实现平滑切换。
  const createBlockRef = useRef<HTMLDivElement>(null);
  const [createBlockH, setCreateBlockH] = useState<number>(0);
  const [s1BlockH, setS1BlockH] = useState<number>(0);
  useEffect(() => {
    const createEl = createBlockRef.current;
    if (!createEl) return;
    const ro = new ResizeObserver(() => setCreateBlockH(createEl.offsetHeight));
    ro.observe(createEl);
    setCreateBlockH(createEl.offsetHeight);
    return () => ro.disconnect();
  }, []);
  // 高度精确等于激活块，切换时才能过渡（auto 高度过渡不了）
  const activeBlockH = mode === 'create' ? createBlockH : s1BlockH;

  // 背景视频高度跟随 Hero 区域。移动端不渲染背景，测量与滚动监听一并跳过。
  const sectionRef = useRef<HTMLElement>(null);
  const [heroHeight, setHeroHeight] = useState<number>(0);
  // Portal 让固定背景不受父级层叠上下文影响。
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || isSmScreen) return;
    const ro = new ResizeObserver(() => setHeroHeight(el.offsetHeight));
    ro.observe(el);
    setHeroHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [isSmScreen]);

  // 滚动后淡出背景视频。
  const [scrollOpacity, setScrollOpacity] = useState(1);
  useEffect(() => {
    const scroller = document.getElementById('scrollableDiv');
    if (!scroller || isSmScreen) return;
    const onScroll = () => {
      const fadeDistance = (heroHeight || 320) * 0.3;
      const next = Math.max(0, 1 - scroller.scrollTop / fadeDistance);
      setScrollOpacity(next);
    };
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [heroHeight, isSmScreen]);

  // 背景视频用 fixed 横跨侧边栏，但高度需与 hero section 一致（而非满屏）：

  const modeTabs: { value: HeroMode; label: string }[] = [
    { value: 'create', label: t('home.hero.tabs.create') },
    { value: 'agent', label: t('home.hero.tabs.agent') },
  ];

  return (
    <section ref={sectionRef} className="relative z-10 overflow-hidden sm:z-[30]">
      {mounted &&
        !isSmScreen &&
        createPortal(
          <>
            {[
              {
                key: 'create',
                src: HOME_HERO_BG_SRC_CREATE,
                poster: HOME_HERO_POSTER_SRC_CREATE,
                active: mode === 'create',
              },
              {
                key: 'agent',
                src: HOME_HERO_BG_SRC_AGENT,
                poster: HOME_HERO_POSTER_SRC_AGENT,
                active: mode === 'agent',
              },
            ].map(({ key, src, poster, active }) => (
              <video
                key={key}
                src={src}
                poster={poster}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                style={{ height: heroHeight || undefined, opacity: active ? 0.88 : 0 }}
                className="pointer-events-none fixed inset-x-0 top-0 z-[-1] w-full object-cover transition-opacity duration-300"
              />
            ))}
            {/* 底部渐变和滚动蒙版 */}
            <div
              className="pointer-events-none fixed inset-x-0 top-0 z-[-1] w-full bg-gradient-to-b from-transparent to-[#0A0D11]"
              style={{ height: heroHeight || undefined }}
            />
            {/* 滚动遮罩 */}
            <div
              className="pointer-events-none fixed inset-x-0 top-0 z-[-1] w-full transition-opacity duration-150"
              style={{ height: heroHeight || undefined, backgroundColor: '#0A0D11', opacity: 1 - scrollOpacity }}
            />
          </>,
          document.body,
        )}

      {/* Hero 内容 */}
      <div className="relative z-10 mx-[16%] flex flex-col items-center gap-6 pt-[8vh] pb-[calc(8vh-48px)] sm:mx-0 sm:p-0">
        <h1 className="text-center font-Poppins font-semibold text-[2.5rem] text-white leading-tight sm:hidden sm:text-2xl">
          {t.rich('home.hero.title', {
            i: (chunks) => <span className="font-normal font-serif italic">{chunks}</span>,
          })}
        </h1>

        {/* 模式切换 */}
        <div className="flex items-center rounded-full bg-white/[0.08] p-0.5 backdrop-blur-[16px] sm:hidden">
          {modeTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setMode(tab.value)}
              className={cn(
                'flex h-10 w-[120px] items-center justify-center rounded-full px-4 font-Poppins font-medium text-base text-white transition-colors',
                mode === tab.value ? 'border border-white/[0.16] bg-white/[0.16]' : 'border border-transparent',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* create 与 S1 叠在同一格 */}
        <div
          className="grid w-full grid-cols-1 transition-[height] duration-300"
          style={{ height: activeBlockH || undefined }}
        >
          {/* 表单内有 position: fixed 的浮动栏，外层不能用 transform / backdrop-filter，否则会成为它们的包含块 */}
          <div
            ref={createBlockRef}
            className={cn(
              'col-start-1 row-start-1 w-full self-start pb-8 transition-opacity duration-300',
              mode === 'create' ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            aria-hidden={mode !== 'create'}
          >
            <HomeGenerationForm />
          </div>

          {/* 移动端进不了 agent 模式（切换 tab 已隐藏），不渲染省掉角色列表的两个请求 */}
          {!isSmScreen && <HomeS1Roles isActive={mode === 'agent'} onHeightChange={setS1BlockH} />}
        </div>
      </div>
    </section>
  );
};
